import { createContext } from 'preact'
import { useCallback, useState, useEffect, useRef, useContext } from 'preact/hooks'
import type { AthosContextValue } from 'libs/athos/athos.types'

const initValue: AthosContextValue = {
  isLoading: false,
  results: [],
  options: new Map(),
  params: {},
  refine: () => {},
  clear: () => {}
}
const AthosContext = createContext<AthosContextValue>(initValue)

/** Same opt-in flag as AthosFetcher's raw-response logging (`localStorage.athosDebug`). */
function logAthosNormalised(source: string, data: any): void {
  if (typeof window === 'undefined' || window.localStorage?.getItem('athosDebug') !== 'true') return
  console.groupCollapsed(`[Athos] ${source} → normalised`)
  console.log(JSON.parse(JSON.stringify(data)))
  console.groupEnd()
}

function parseOptionFromURL(params: URLSearchParams, key: string): unknown {
  const value = params.get(key)
  if (!value) return null

  switch (key) {
    case 'filters': {
      const filterArray = []
      const parts = value.split(',')
      let currentFilter = null

      for (const part of parts) {
        if (!part) continue

        const colonIndex = part.indexOf(':')
        if (colonIndex > 0 && colonIndex < part.length - 1) {
          if (currentFilter) {
            filterArray.push(currentFilter)
          }
          currentFilter = part
        } else if (currentFilter) {
          currentFilter += ',' + part
        } else {
          continue
        }
      }

      if (currentFilter) {
        filterArray.push(currentFilter)
      }

      return filterArray.length > 0 ? new Set(filterArray) : null
    }
    case 'page': {
      const pageNum = parseInt(value)
      return isNaN(pageNum) ? null : pageNum
    }
    default:
      return value
  }
}

function getInitialOptionsFromURL(): Record<string, unknown> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const initialOptions: Record<string, unknown> = {}

  for (const key of params.keys()) {
    const value = parseOptionFromURL(params, key)
    if (value !== null) {
      initialOptions[key] = value
    }
  }

  return initialOptions
}

interface AthosFetcherInitialiser {
  options?: Record<string, unknown>
  onFetch?: (data: any, options: Map<string, unknown>) => any
  runOnMount?: boolean
}

export function useAthosFetcher(
  fetcher: any,
  initialiser: AthosFetcherInitialiser = {
    options: {},
    runOnMount: true
  }
): AthosContextValue {
  const { options: _options = {}, onFetch, runOnMount = true } = initialiser
  const isMounted = useRef(runOnMount)
  const [isLoading, setIsLoading] = useState(false)

  const urlOptions = useRef(getInitialOptionsFromURL())
  const mergedOptions = useRef({ ...urlOptions.current, ..._options })

  const [options, setOptions] = useState({ map: new Map(Object.entries(mergedOptions.current)) })
  const [results, setResults] = useState({})
  const params = fetcher?.params
  const refine = useCallback(
    (key: string, value: unknown) => {
      if (!value) return
      const currentValue = options.map.get(key)

      if (value instanceof Set && currentValue instanceof Set) {
        if (value.size === currentValue.size && [...value].every(item => currentValue.has(item))) {
          return
        }
      } else if (value === currentValue) {
        return
      }

      setOptions((prev) => {
        const next = new Map(prev.map)
        next.set(key, value)
        return { map: next }
      })
    },
    [options, setOptions]
  )

  const clear = useCallback(
    (key?: string) => {
      if (!key) return
      setOptions((prev) => {
        const next = new Map(prev.map)
        next.delete(key)
        return { map: next }
      })
    },
    [options, setOptions]
  )

  useEffect(() => {
    function handleResultUpdate() {
      if (!isMounted.current) {
        isMounted.current = true
        return
      }
      setIsLoading(true)
      handleFetch()
    }

    const controller = new AbortController()
    handleResultUpdate()

    async function handleFetch() {
      try {
        fetcher.updateParams(Object.fromEntries(options.map.entries()))
        const data = await fetcher.fetch(controller)

        if (controller.signal.aborted) {
          return
        }

        if (onFetch) {
          const normalisedResults = await onFetch(data, options.map)
          if (controller.signal.aborted) {
            return
          }
          logAthosNormalised(fetcher.constructor.name, normalisedResults)
          setResults(normalisedResults)
        } else {
          setResults(data)
        }
        setIsLoading(false)
      } catch (error: any) {
        if (controller.signal.aborted || error?.name === 'AbortError') {
          return
        }
        setIsLoading(false)
      }
    }
    return () => {
      controller.abort()
    }
  }, [options])

  return {
    isLoading,
    results,
    options: options.map,
    params,
    setOptions,
    refine,
    clear
  }
}

interface AthosProviderProps {
  fetcher: any
  onFetch?: (data: any, options: Map<string, unknown>) => any
  children?: any
  options?: Record<string, unknown>
  runOnMount?: boolean
}

export function AthosProvider({ fetcher, onFetch, children, options, runOnMount }: AthosProviderProps) {
  const value = useAthosFetcher(fetcher, {
    onFetch,
    options,
    runOnMount
  })

  return <AthosContext.Provider value={value}>{children}</AthosContext.Provider>
}

export function useAthosContext(): AthosContextValue {
  return useContext(AthosContext)
}

type AthosFetcherConstructor = new (category?: string) => any

export function generateAthosProvider(
  FetcherClass: AthosFetcherConstructor,
  onFetch?: (data: any, options: Map<string, unknown>) => any
) {
  return function ({ children, category, runOnMount, ...options }: any) {
    const fetcherRef = useRef(new FetcherClass(category))
    return (
      <AthosProvider fetcher={fetcherRef.current} onFetch={onFetch} runOnMount={runOnMount} options={options}>
        {children}
      </AthosProvider>
    )
  }
}
